//================================================================
//  Component: Add Subnet
//================================================================

//  Purpose: Add a subnet to the sites collection

//  Properties:
//    - None

//  AddRegionSupernet:
//    <AddRegionSupernet></AddRegionSupernet>    

//================================================================


//Libraries
import React, { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Netmask } from 'netmask';

//Contexts
import { GetUser, SetToast } from '../../../../Library/GlobalContexts';

//Functions
import WriteDocument from '../../../../Library/WriteDocument';

// Images
import IconAdd from '../../../../Components/Images/Icon_Add_White.svg';

export default function AddRegionSupernet() {

    //------------------------------------------------------
    //  useContexts
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const setToast = useContext(SetToast);

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Open or Close the 'Add Region' side pane
    const [addRegionSubnet, setAddRegionSubnet] = useState(false);

    // Duplication check -> 'onload', 'pending', 'failed' & 'complete'
    const [supernetStatus, setSupernetStatus] = useState('onload');

    // --------------------------------------------------
    //  Form State
    // --------------------------------------------------

    // https://react-hook-form.com/get-started
    const {
        register,
        handleSubmit,
        reset,
        setValue,
        setError,
        clearErrors,
        formState: { isSubmitSuccessful, errors },
    } = useForm();

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    function validateSupernet(value) {

        try {

            setValue('supernet', value)

            // Clear errors and values from previous validation
            clearErrors('supernet');

            // Check for a valid CIDR before validating...
            // https://blog.markhatton.co.uk/2011/03/15/regular-expressions-for-ip-addresses-cidr-ranges-and-hostnames/
            if (/^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])(\/(3[0-2]|[1-2][0-9]|[0-9]))$/.test(value) === false) {

                setError('supernet', {
                    message: `Invalid CIDR provided! Expected format, 'XXX.XXX.XXX.XXX/XX'`
                });

                setSupernetStatus('onload');

                return;

            }

            //------------------------------------------------------
            //  Get subnet information
            //------------------------------------------------------

            // https://networkcalc.com/api/docs
            // Benno 14-04-2025 ~ Discussed with Jeffey & Alex, they are comfortable with an external dependency.
            const newSubnet = new Netmask(value);

            // Save to form
            setValue('subnetmask', newSubnet.mask);
            setValue('assignablehosts', newSubnet.size - 2); // Remove broadcast IP's
            setValue('hostmin', newSubnet.first);
            setValue('hostmax', newSubnet.last);

        } catch (error) {

            console.log(error);
            setSupernetStatus('failed');
            clearErrors('supernet');

            // Set error on toast
            setToast({
                'type': 'error',
                'message': 'Failed to complete validation request. If the error persists contact support.',
            });
        }

    }

    const onSubmit = (formData) => {

        // Open the toast
        setToast({
            'type': 'pending',
            'message': 'Creating region please wait...',
        });

        // Supernet IP's can overlap, so we have added the region
        const documentid = `${formData.supernet.replace('/', '-')}-${btoa(formData.region)}`;

        const documentObject = {
            'subnetid': documentid,
            'type': 'region',
            'region': formData.region,
            'supernet': formData.supernet,
            'supernetid': documentid,
            'subnetip': formData.supernet,
            'description': formData.description.trim(),
            'vlan': 'NA',
            'subnetmask': formData.subnetmask,
            'assignablehosts': formData.assignablehosts,
            'hostmin': formData.hostmin,
            'hostmax': formData.hostmax,
            'createddate': new Date(),
            'createdby': {
                'email': getUser.emailaddress,
                'givenname': getUser.givenname,
                'surname': getUser.surname,
            },
            'siteid': formData.siteid,
            'lastmodifieddate': new Date(), // Timestamp, added by the system not a user editable field
            'lastmodifiedby': {
                'email': getUser.emailaddress,
                'givenname': getUser.givenname,
                'surname': getUser.surname,
            },
            'additionalinfo': formData.additionalinfo.trim(),
            'status': 'active',
        };

        WriteDocument('subnets', documentid, documentObject, false).then(() => {

            // Close Side Pane
            setAddRegionSubnet(false);

            // Show Success Toast
            setToast({
                'type': 'success',
                'message':
                    <>
                        Region/Supernet added. <a href={process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285' ? `https://devinventory.digitalservices.lendlease.com/inventory/subnet/${documentid}?view=overview` : process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396' ? `https://uatinventory.digitalservices.lendlease.com/inventory/subnet/${documentid}?view=overview` : `https://inventory.digitalservices.lendlease.com/inventory/subnet/${documentid}?view=overview`} target='_blank' rel='noopener noreferrer'>{documentObject.supernet}</a>.
                    </>
            });

        }).catch((error) => {

            console.log('error', error);

            // Set error on toast
            setToast({
                'type': 'error',
                'message': 'Failed to create region, refresh the page and try again.',
            });

        });

    };

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Reset form onclose
    useEffect(() => {

        if (isSubmitSuccessful) {
            reset();
        };

    }, [isSubmitSuccessful, reset]);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    if (addRegionSubnet === false) return (

        <button className='Primary-Button whitespace-nowrap flex flex-row gap-2' onClick={() => setAddRegionSubnet(true)}>
            <img src={IconAdd} alt='Add'></img> Add Region/Supernet
        </button>

    );

    return (
        <>

            {/* ======== Keeps the button is visible during slide animation ========= */}
            <button className='Primary-Button whitespace-nowrap flex flex-row gap-2' onClick={() => setAddRegionSubnet(true)}>
                <img src={IconAdd} alt='Add'></img> Add Region/Supernet
            </button>

            {/* =========================================================== */}
            {/*  Add Region Pane                                         */}
            {/* =========================================================== */}

            <div className='Pane-Background'>
                <dialog className='Pane-Container'>

                    {/*  ========= Add Region Supernet Form * ========= */}
                    <form className='grid grid-rows-[70px_1fr_70px] items-center justify-between w-full overflow-hidden' onSubmit={handleSubmit(onSubmit)}>

                        {/* Header */}
                        <div className='flex flex-col gap-2 p-4'>
                            <h4 className='text-[22px] p-0 m-0'> Add Region/Supernet </h4>
                        </div>

                        <div className='w-[50vw] h-full flex flex-col overflow-y-auto p-4 px-4 py-2 border-solid border shadow-inner'>

                            {/*  ========= 1. Region * ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>1. Region <span className='text-[#C4314B]'>*</span></label>

                                <div className='flex flex-row gap-2 items-center'>

                                    <input
                                        className={errors.region ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                        type='text'
                                        placeholder='AUST'
                                        autoComplete='no'
                                        {...register(
                                            'region', {
                                            required: 'This is a required field.',
                                            maxLength: 50,
                                        })}
                                        disabled={supernetStatus === 'pending' ? true : false}
                                    ></input>

                                </div>

                                {errors.region && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.region?.message}</p>}
                            </div>

                            {/*  ========= 2. Supernet * ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>2. Supernet <span className='text-[#C4314B]'>*</span></label>
                                <input
                                    className={errors.supernet ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    type='text'
                                    placeholder='172.19.0.0/16'
                                    autoComplete='no'
                                    disabled={supernetStatus === 'pending' ? true : false}
                                    {...register(
                                        'supernet', {
                                        required: 'This is a required field.',
                                    })}
                                    onChange={(e) => validateSupernet(e.target.value)}
                                ></input>
                                {errors.supernet && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.supernet?.message}</p>}
                            </div>

                            {/*  ========= 3. Site ID  ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>3. Site ID <span className='text-[#C4314B]'>*</span></label>
                                <input
                                    className={errors.siteid ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    type='text'
                                    placeholder='NA'
                                    autoComplete='no'
                                    {...register(
                                        'siteid', {
                                        required: 'This is a required field.',
                                    })}
                                ></input>
                                {errors.siteid && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.siteid?.message}</p>}
                            </div>

                            {/*  ========= 3. Description * ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>3. Description <span className='text-[#C4314B]'>*</span></label>
                                <textarea
                                    className={errors.description ? 'Input-Field-TextArea-Error' : 'Input-Field-TextArea'}
                                    style={{ height: '110px' }}
                                    type='text'
                                    placeholder='Add a description'
                                    {...register('description', {
                                        required: 'This is a required field.',
                                        maxLength: 200,
                                    })}
                                ></textarea>

                                {errors.description && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.description?.message}</p>}
                            </div>

                            {/*  ========= 4. Netmask  ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>4. Netmask <span className='text-[#C4314B]'>*</span></label>
                                <input
                                    className={errors.subnetmask ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    type='text'
                                    placeholder='255.255.0.0'
                                    autoComplete='no'
                                    disabled={true}
                                    {...register(
                                        'subnetmask', {
                                    })}
                                ></input>
                                {errors.subnetmask && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.subnetmask?.message}</p>}
                            </div>

                            {/*  ========= 5. Hosts/Net * ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>5. Hosts/Net <span className='text-[#C4314B]'>*</span></label>
                                <input
                                    className={errors.assignablehosts ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    type='number'
                                    placeholder='65534'
                                    autoComplete='no'
                                    disabled={true}
                                    {...register(
                                        'assignablehosts', {
                                    })}
                                ></input>
                                {errors.assignablehosts && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.assignablehosts?.message}</p>}
                            </div>

                            {/*  ========= 6. HostMin ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>6. HostMin <span className='text-[#C4314B]'>*</span></label>
                                <input
                                    className={errors.hostmin ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    type='text'
                                    placeholder='172.19.0.1'
                                    autoComplete='no'
                                    disabled={true}
                                    {...register(
                                        'hostmin', {
                                    })}
                                ></input>
                                {errors.hostmin && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.hostmin?.message}</p>}
                            </div>

                            {/*  ========= 7. HostMax * ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>7. HostMax <span className='text-[#C4314B]'>*</span></label>
                                <input
                                    className={errors.hostmax ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    type='text'
                                    placeholder='172.19.255.254'
                                    autoComplete='no'
                                    disabled={true}
                                    {...register(
                                        'hostmax', {
                                    })}
                                ></input>
                                {errors.hostmax && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.hostmax?.message}</p>}
                            </div>

                            {/*  ========= 8. Additional Info * ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>8. Additional Info <span className='text-[#C4314B]'>*</span></label>
                                <textarea
                                    className={errors.additionalinfo ? 'Input-Field-TextArea-Error' : 'Input-Field-TextArea'}
                                    style={{ height: '110px' }}
                                    type='text'
                                    placeholder='VRF, VPC, Zone…'
                                    {...register('additionalinfo', {
                                        required: 'This is a required field.',
                                        maxLength: 200,
                                    })}
                                ></textarea>

                                {errors.additionalinfo && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.additionalinfo?.message}</p>}
                            </div>

                        </div>

                        {/*  ========= Button Container ========= */}
                        <div className='flex flex-row gap-2 px-4 py-3'>

                            <div className='flex flex-row gap-2'>

                                {/* Submit */}
                                <button
                                    className='Primary-Button whitespace-nowrap'
                                    disabled={false}
                                    type='submit'
                                    value='submit'
                                >Submit</button>

                                {/* Cancel */}
                                <button className='Secondary-Button whitespace-nowrap' onClick={() => {

                                    // close side pane
                                    reset();
                                    setSupernetStatus('onload');
                                    setAddRegionSubnet(false);

                                }}>Cancel</button>

                            </div>

                        </div>

                    </form>

                </dialog>
            </div>
        </>
    )
}
